import React, { useContext, useEffect, useRef, useState } from 'react';
import { IProps } from './index.types';

import {
  Body,
  FormsSection,
  Title,
  Container,
  ContentLayout,
  FormColumn,
  ErrorSidebar,
  ErrorSummaryContainer,
  ErrorSummaryList,
  ErrorSummaryTitle,
  ERROR_SIDEBAR_WIDTH
} from './index.styled';
import CreateEditOccurrenceHeader from './components/header';
import { useNavigate, useParams } from 'react-router-dom';
import { Divider } from '../../../../../shared/styles';
import { OccurrenceContext } from '../../../providers/OccurrenceProvider';
import { useTheme } from 'styled-components';
import CreateEditOcurrenceDetailsFormComponent from './components/details_form';
import CreateEditOcurrenceContextInfoFormComponent from './components/context_info_form';
import CreateEditOcurrenceTransportsFormComponent from './components/transports_form';
import CreateEditOcurrenceAnimalVictimsFormComponent from './components/animal_victims_form';
import CreateEditOcurrenceVictimsFormComponent from './components/victims_form';
import Loading from '../../../../../components/Loading';
import { reportErrorHandler } from '../../../../../utils/GlobalErrorHandler';
import { showAlert } from '../../../../../shared/components/alert';
import ModalError from '../../../../../shared/components/modal_error';
import ModalSucesso from '../../../../../shared/components/modal_successo';
import SuccessOccurrenceModal from '../../../../../components/SuccessOccurrenceModal';
import { EOccurrenceStatus } from '../../../../../shared/enums/ocurrenceStatus';
import OccurrenceReviewModal from './components/review_modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import OccurrenceChangesModal from '../../../../../components/OccurrenceChangesModal';

type ErrorSummaryItem = {
  id: string;
  label: string;
};

const CreateEditOccurrence: React.FC<IProps> = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { token } = useSelector((state: RootState) => state.clickState);

  const [isVisibleModal, setVisibleModal] = useState<boolean>(false);
  const [occurrenceId, setOccurrenceId] = useState<string>('');
  const [successOccurrenceModalTitle, setSuccessOccurrenceModalTitle] =
    useState<string>('');
  const [successOccurrenceModalSubTitle, setSuccessOccurrenceModalSubTitle] =
    useState<string>('');
  const [isReviewModalOpen, setReviewModalOpen] = useState<boolean>(false);
  const [errorSummaryItems, setErrorSummaryItems] = useState<
    ErrorSummaryItem[]
  >([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isChangesModalOpen, setChangesModalOpen] = useState(false);
  const shouldAutoScrollErrorsRef = useRef(false);
  const hasAttemptedSubmitRef = useRef(false);
  const pendingErrorFieldRef = useRef<HTMLElement | null>(null);

  const getBackendValidationMessages = (error: any) => {
    if (Array.isArray(error?.payload?.message)) {
      return error.payload.message
        .map((message: unknown) => String(message))
        .filter(Boolean);
    }

    if (typeof error?.payload?.message === 'string') {
      return [error.payload.message];
    }

    return [] as string[];
  };

  const renderBackendValidationDescription = (fallback: string, error: any) => {
    const validationMessages = getBackendValidationMessages(error);

    if (validationMessages.length === 0) {
      return fallback;
    }

    return (
      <>
        <p>{fallback}</p>
        <ul>
          {validationMessages.map((message: string, index: number) => (
            <li key={`${index}-${message}`}>{message}</li>
          ))}
        </ul>
      </>
    );
  };

  const {
    createEditOccurrenceUsecase,
    createEditOccurrenceState,
    previousCreateEditOccurrenceState
  } = useContext(OccurrenceContext);

  const triggerFormSubmit = () => {
    createEditOccurrenceState.detailsForm.ref.current?.submitForm();
    createEditOccurrenceState.contextInfoForm.ref.current?.submitForm();
    createEditOccurrenceState.transportsForm.forEach(transportForm => {
      transportForm.ref.current?.submitForm();
    });
    createEditOccurrenceState.victimsForm.forEach(victim => {
      victim.ref.current?.submitForm();
    });
    createEditOccurrenceState.animalVictimsForm.forEach(animalVictim => {
      animalVictim.ref.current?.submitForm();
    });
  };

  const errorSelector =
    '.is-invalid, [aria-invalid="true"], .is-invalid-radio-group';

  const getVisibleErrorElements = () => {
    if (typeof document === 'undefined') return [];

    return Array.from(
      document.querySelectorAll<HTMLElement>(errorSelector)
    ).filter(element => element.offsetParent !== null);
  };

  const getErrorSummaryItems = () => {
    const errorElements = getVisibleErrorElements();
    const summaryMap = new Map<string, ErrorSummaryItem>();
    let fallbackIndex = 0;

    errorElements.forEach(element => {
      const fieldContainer = element.closest(
        '[data-form-field]'
      ) as HTMLElement | null;
      const fieldLabel = fieldContainer?.dataset.formLabel?.trim();
      const fieldName = fieldContainer?.dataset.formField?.trim();

      const sectionLabel = (
        element.closest('[data-form-section]') as HTMLElement | null
      )?.dataset.formSection?.trim();
      const blockLabel = (
        element.closest('[data-form-block]') as HTMLElement | null
      )?.dataset.formBlock?.trim();

      const fallbackLabel =
        element.getAttribute('aria-label') ||
        element.getAttribute('placeholder') ||
        element.getAttribute('name') ||
        element.getAttribute('id') ||
        'Campo inválido';

      const label = fieldLabel || fallbackLabel;
      const summaryText = [sectionLabel, blockLabel, label]
        .filter(Boolean)
        .join(' - ');
      const summaryKey = [sectionLabel, blockLabel, fieldName || label]
        .filter(Boolean)
        .join('|');

      const anchorSeed = [sectionLabel, blockLabel, fieldName || label]
        .filter(Boolean)
        .join('-');
      const anchorTarget = fieldContainer || element;
      const anchorId = getOrCreateElementAnchor(
        anchorTarget,
        anchorSeed,
        fallbackIndex
      );

      summaryMap.set(summaryKey || summaryText, {
        id: anchorId,
        label: summaryText
      });
      fallbackIndex += 1;
    });

    return Array.from(summaryMap.values());
  };

  const getOrCreateElementAnchor = (
    element: HTMLElement,
    seed: string,
    index: number
  ) => {
    const existingAnchor = element.dataset.formAnchor || element.id;

    if (existingAnchor) {
      element.id = existingAnchor;
      return existingAnchor;
    }

    const safeSeed = seed
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    const baseId = safeSeed ? `field-${safeSeed}` : `field-${index}`;

    let candidate = baseId;
    let suffix = 1;

    while (document.getElementById(candidate)) {
      candidate = `${baseId}-${suffix}`;
      suffix += 1;
    }

    element.dataset.formAnchor = candidate;
    element.id = candidate;

    return candidate;
  };

  const refreshErrorSummary = () => {
    if (!hasAttemptedSubmitRef.current) return;

    setErrorSummaryItems(getErrorSummaryItems());
  };

  const scrollToErrorElement = (element: HTMLElement) => {
    const scrollTarget = element.closest('.form-group') || element;

    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus?.();
  };

  const scrollToFieldById = (id: string) => {
    if (typeof document === 'undefined') return;

    const fieldElement = document.getElementById(id);
    if (!fieldElement) return;

    const invalidField = fieldElement.querySelector<HTMLElement>(errorSelector);

    scrollToErrorElement(invalidField || fieldElement);
  };

  const isElementInvalid = (element: HTMLElement | null) =>
    !!element?.matches(errorSelector);

  const findNextErrorElement = (element: HTMLElement) => {
    const errorElements = getVisibleErrorElements();

    if (!errorElements.length) return null;

    if (!document.body.contains(element)) {
      return errorElements[0] ?? null;
    }

    const nextError = errorElements.find(
      errorElement =>
        element.compareDocumentPosition(errorElement) &
        Node.DOCUMENT_POSITION_FOLLOWING
    );

    return nextError || errorElements[0] || null;
  };

  const scrollToFirstError = () => {
    if (typeof document === 'undefined') return;

    const firstVisibleError = getVisibleErrorElements()[0];

    if (!firstVisibleError) return;

    scrollToErrorElement(firstVisibleError);
  };

  const onConfirm = async () => {
    triggerFormSubmit();
    await createEditOccurrenceUsecase.handleFormSubmit();
  };

  const onOpenReviewModal = async () => {
    const isValid =
      await createEditOccurrenceUsecase.validateFormWithValidationSchema();

    if (!isValid) {
      triggerFormSubmit();
      shouldAutoScrollErrorsRef.current = true;
      hasAttemptedSubmitRef.current = true;
      setHasAttemptedSubmit(true);
      pendingErrorFieldRef.current = null;
      setTimeout(() => {
        refreshErrorSummary();
        scrollToFirstError();
      }, 0);
      return;
    }

    setHasAttemptedSubmit(false);
    hasAttemptedSubmitRef.current = false;
    setErrorSummaryItems([]);
    setReviewModalOpen(true);
  };

  const onConfirmReview = async () => {
    setReviewModalOpen(false);
    await onConfirm();
  };

  useEffect(() => {
    createEditOccurrenceUsecase.init(id);

    if (!id) {
      createEditOccurrenceUsecase.restoreDraft();
    }

    return () => {
      createEditOccurrenceUsecase.resetState();
    };
  }, []);

  useEffect(() => {
    if (id) return;

    createEditOccurrenceUsecase.persistDraft();
  }, [
    id,
    createEditOccurrenceState.detailsForm,
    createEditOccurrenceState.contextInfoForm,
    createEditOccurrenceState.transportsForm,
    createEditOccurrenceState.victimsForm,
    createEditOccurrenceState.animalVictimsForm
  ]);

  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (!shouldAutoScrollErrorsRef.current) return;
      if (!(event.target instanceof HTMLElement)) return;

      const errorElement = event.target.closest(errorSelector);
      if (errorElement) {
        pendingErrorFieldRef.current = errorElement as HTMLElement;
      }
    };

    const handleFieldUpdate = (event: Event) => {
      if (!shouldAutoScrollErrorsRef.current) return;
      if (!(event.target instanceof HTMLElement)) return;

      const pendingErrorField = pendingErrorFieldRef.current;
      if (!pendingErrorField) return;

      if (
        !pendingErrorField.contains(event.target) &&
        !event.target.contains(pendingErrorField)
      ) {
        return;
      }

      setTimeout(() => {
        if (!pendingErrorFieldRef.current) return;
        if (isElementInvalid(pendingErrorField)) return;

        const nextError = findNextErrorElement(pendingErrorField);
        pendingErrorFieldRef.current = null;

        if (!nextError) {
          shouldAutoScrollErrorsRef.current = false;
          return;
        }

        scrollToErrorElement(nextError);
      }, 0);
    };

    const handleFormUpdate = () => {
      if (!hasAttemptedSubmitRef.current) return;

      setTimeout(refreshErrorSummary, 0);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('change', handleFieldUpdate, true);
    document.addEventListener('blur', handleFieldUpdate, true);
    document.addEventListener('change', handleFormUpdate, true);
    document.addEventListener('blur', handleFormUpdate, true);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('change', handleFieldUpdate, true);
      document.removeEventListener('blur', handleFieldUpdate, true);
      document.removeEventListener('change', handleFormUpdate, true);
      document.removeEventListener('blur', handleFormUpdate, true);
    };
  }, []);

  useEffect(() => {
    createEditOccurrenceUsecase.countVictims();
  }, [createEditOccurrenceState.victimsForm]);

  useEffect(() => {
    createEditOccurrenceUsecase.countAnimalVictims();
  }, [createEditOccurrenceState.animalVictimsForm]);

  useEffect(() => {
    if (
      createEditOccurrenceState.occurrenceRequestStatus.status !=
      previousCreateEditOccurrenceState?.occurrenceRequestStatus.status
    ) {
      createEditOccurrenceState.occurrenceRequestStatus.maybeMap({
        failed: error => {
          reportErrorHandler({
            error,
            context: {
              reportLocation: {
                filePath:
                  '/pages/RefactorOccurrences/presentation/views/create_edit/index.tsx',
                lineNumber: 0,
                functionName: 'CreateEditOccurrence'
              }
            },
            msgContext: `Error on getOccurrenceForEdit | OccurrenceId: ${id}`
          });

          showAlert({
            onDismiss: () => {
              navigate('/occurrences');
            },
            children: (
              <ModalError
                title="Erro ao carregar ocorrência"
                description="Ocorreu um erro ao carregar a ocorrência, tente novamente mais tarde!"
              />
            )
          });
        }
      });
    }
  }, [createEditOccurrenceState.occurrenceRequestStatus]);

  useEffect(() => {
    if (
      createEditOccurrenceState.editOccurrenceRequestStatus.status !=
      previousCreateEditOccurrenceState?.editOccurrenceRequestStatus.status
    ) {
      createEditOccurrenceState.editOccurrenceRequestStatus.maybeMap({
        failed: error => {
          reportErrorHandler({
            error,
            context: {
              reportLocation: {
                filePath:
                  '/pages/RefactorOccurrences/presentation/views/create_edit/index.tsx',
                lineNumber: 0,
                functionName: 'CreateEditOccurrence'
              }
            },
            msgContext: `Error on editOccurrenceById | OccurrenceId: ${id}`
          });

          showAlert({
            onDismiss: () => {},
            children: (
              <ModalError
                title="Erro ao editar ocorrência"
                description={renderBackendValidationDescription(
                  'Verifique se todos os campos obrigatórios foram preechidos e tente novamente!',
                  error
                )}
              />
            )
          });
        },
        succeeded: _ => {
          createEditOccurrenceState.occurrenceRequestStatus.maybeMap({
            succeeded(data) {
              if (data.status === EOccurrenceStatus.Waiting) {
                setOccurrenceId(data.id);
                setSuccessOccurrenceModalTitle('Ocorrência aprovada');
                setSuccessOccurrenceModalSubTitle(
                  'A ocorrência foi aprovada com sucesso.'
                );
                setVisibleModal(true);
              } else {
                showAlert({
                  onDismiss: () => {
                    navigate('/occurrences');
                  },
                  children: (
                    <ModalSucesso
                      title="Sucesso ao editar ocorrência"
                      description="Ocorrência editada com sucesso!"
                    />
                  )
                });
              }
            }
          });
        }
      });
    }
  }, [createEditOccurrenceState.editOccurrenceRequestStatus]);

  useEffect(() => {
    if (
      createEditOccurrenceState.createOccurrenceRequestStatus.status !=
      previousCreateEditOccurrenceState?.createOccurrenceRequestStatus.status
    ) {
      createEditOccurrenceState.createOccurrenceRequestStatus.maybeMap({
        failed: error => {
          reportErrorHandler({
            error,
            context: {
              reportLocation: {
                filePath:
                  '/pages/RefactorOccurrences/presentation/views/create_edit/index.tsx',
                lineNumber: 0,
                functionName: 'CreateEditOccurrence'
              }
            },
            msgContext: `Error on createOccurence | OccurrenceId: ${id}`
          });

          showAlert({
            onDismiss: () => {},
            children: (
              <ModalError
                title="Erro ao criar ocorrência"
                description={renderBackendValidationDescription(
                  'Ocorreu um erro ao criar a ocorrência, tente novamente!',
                  error
                )}
              />
            )
          });
        },
        succeeded: data => {
          if (!id) {
            createEditOccurrenceUsecase.clearDraft();
          }
          setOccurrenceId(data.id);
          setSuccessOccurrenceModalTitle('Ocorrência registrada');
          setSuccessOccurrenceModalSubTitle(
            'A ocorrência foi registrada com sucesso.'
          );
          setVisibleModal(true);
        }
      });
    }
  }, [createEditOccurrenceState.createOccurrenceRequestStatus]);

  const isErrorSummaryVisible =
    hasAttemptedSubmit && errorSummaryItems.length > 0;

  const handleClearDraft = () => {
    createEditOccurrenceUsecase.clearDraft();
    createEditOccurrenceUsecase.clearDraftedForms();
    setHasAttemptedSubmit(false);
    hasAttemptedSubmitRef.current = false;
    setErrorSummaryItems([]);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const offset = isErrorSummaryVisible
      ? `calc(${ERROR_SIDEBAR_WIDTH}px + ${theme.spacing.lg})`
      : '0px';

    document.documentElement.style.setProperty(
      '--occurrence-error-sidebar-offset',
      offset
    );

    return () => {
      document.documentElement.style.removeProperty(
        '--occurrence-error-sidebar-offset'
      );
    };
  }, [isErrorSummaryVisible, theme.spacing.lg]);

  const formContent = (
    <Container $withSidebar={isErrorSummaryVisible}>
      <Title>Registrar ocorrência</Title>
      <FormsSection>
        <CreateEditOcurrenceDetailsFormComponent />
      </FormsSection>
      <FormsSection>
        <CreateEditOcurrenceContextInfoFormComponent />
      </FormsSection>
      <FormsSection>
        <CreateEditOcurrenceTransportsFormComponent />
      </FormsSection>
      <FormsSection>
        <CreateEditOcurrenceVictimsFormComponent />
      </FormsSection>
      <FormsSection>
        <CreateEditOcurrenceAnimalVictimsFormComponent />
      </FormsSection>
    </Container>
  );

  const formBody = () => (
    <>
      <CreateEditOccurrenceHeader
        newOccurrence={!id}
        onConfirm={onOpenReviewModal}
        onBack={() => {
          navigate('/occurrences');
        }}
        onClear={!id ? handleClearDraft : undefined}
        onOpenChanges={id ? () => setChangesModalOpen(true) : undefined}
        withSidebar={isErrorSummaryVisible}
      />
      <ContentLayout>
        <FormColumn>
          <Divider height={48} />
          {formContent}
        </FormColumn>
        {isErrorSummaryVisible ? (
          <ErrorSidebar>
            <ErrorSummaryContainer role="alert" aria-live="polite">
              <ErrorSummaryTitle>Campos com erro</ErrorSummaryTitle>
              <ErrorSummaryList>
                {errorSummaryItems.map(item => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={event => {
                        event.preventDefault();
                        scrollToFieldById(item.id);
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ErrorSummaryList>
            </ErrorSummaryContainer>
          </ErrorSidebar>
        ) : null}
      </ContentLayout>

      <SuccessOccurrenceModal
        title={successOccurrenceModalTitle}
        subtitle={successOccurrenceModalSubTitle}
        open={isVisibleModal}
        occurrenceId={occurrenceId}
        onClose={() => {
          setVisibleModal(false);
          navigate('/occurrences');
        }}
      />

      <OccurrenceReviewModal
        open={isReviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        onConfirm={onConfirmReview}
        createEditOccurrenceState={createEditOccurrenceState}
      />

      <OccurrenceChangesModal
        open={isChangesModalOpen}
        occurrenceId={id}
        token={token}
        onClose={() => setChangesModalOpen(false)}
      />
    </>
  );

  const renderLoadingScreen = (message: string) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <h2>{message}</h2>
      <Divider width={16} />
      <Loading />
    </div>
  );

  return (
    <Body>
      {id
        ? createEditOccurrenceState.occurrenceRequestStatus.maybeMap({
            loading: () =>
              renderLoadingScreen('Carregando ocorrência, aguarde...'),
            succeeded() {
              return formBody();
            }
          })
        : createEditOccurrenceState.preparingDataRequestStatus.maybeMap({
            loading: () => renderLoadingScreen('Preparando dados, aguarde...'),
            succeeded(_) {
              return formBody();
            }
          })}
    </Body>
  );
};

export default CreateEditOccurrence;
